import React from 'react';
import { SideBar } from '../../../sidebar';
import HeaderBanner from '../../../../common/HeaderBanner';
import '../../../../../styles/components/create-group.scss';
import i18n from '../../../../../i18n';
import {
    ArrowLeft,
    Expand,
    Shrink,
    Loader2,
    Search,
} from 'lucide-react';
import {
    createAttribute,
    getAttribute,
    updateAttribute,
} from '../../../../../models/attributes';
import { toast } from 'react-toastify';
import { getAllCategories } from '../../../../../models/categories';

/* ── Font Awesome icon set for the picker ── */
const FA_ICONS = [
    'fa-home', 'fa-building', 'fa-map-marker', 'fa-map', 'fa-road',
    'fa-ruler', 'fa-ruler-combined', 'fa-ruler-horizontal', 'fa-ruler-vertical',
    'fa-expand', 'fa-compress', 'fa-arrows-alt', 'fa-hashtag', 'fa-tag',
    'fa-tags', 'fa-car', 'fa-truck', 'fa-tachometer', 'fa-cog', 'fa-cogs',
    'fa-wrench', 'fa-gas-pump', 'fa-key', 'fa-calendar', 'fa-calendar-alt',
    'fa-clock', 'fa-hourglass', 'fa-money-bill', 'fa-dollar-sign', 'fa-coins',
    'fa-credit-card', 'fa-percent', 'fa-chart-bar', 'fa-chart-line',
    'fa-chart-pie', 'fa-bed', 'fa-bath', 'fa-shower', 'fa-door-open',
    'fa-door-closed', 'fa-couch', 'fa-chair', 'fa-tv', 'fa-wifi',
    'fa-parking', 'fa-swimming-pool', 'fa-tree', 'fa-leaf',
    'fa-sun', 'fa-bolt', 'fa-fire', 'fa-water', 'fa-snowflake',
    'fa-thermometer', 'fa-wind', 'fa-mountain', 'fa-globe', 'fa-compass',
    'fa-map-signs', 'fa-location-arrow', 'fa-street-view', 'fa-city',
    'fa-warehouse', 'fa-store', 'fa-industry', 'fa-landmark',
    'fa-phone', 'fa-envelope', 'fa-user', 'fa-users', 'fa-id-card',
    'fa-shield-alt', 'fa-lock', 'fa-unlock', 'fa-certificate',
    'fa-award', 'fa-star', 'fa-heart', 'fa-flag', 'fa-info-circle',
    'fa-exclamation-triangle', 'fa-check-circle', 'fa-times-circle',
    'fa-file', 'fa-file-alt', 'fa-file-pdf', 'fa-image',
    'fa-camera', 'fa-video', 'fa-paint-brush', 'fa-palette',
    'fa-tools', 'fa-hammer', 'fa-screwdriver', 'fa-plug',
    'fa-lightbulb', 'fa-battery-full', 'fa-signal', 'fa-weight',
    'fa-balance-scale', 'fa-clipboard', 'fa-list', 'fa-th',
    'fa-barcode', 'fa-qrcode', 'fa-fingerprint', 'fa-eye',
];

export class AttributeCreate extends React.Component {
    constructor(props) {
        super(props);

        /* detect edit mode from URL */
        let editId = null;
        const path = window.location.pathname;
        if (path.includes('/edit/')) {
            editId = path.substring(path.lastIndexOf('/') + 1);
        }

        this.state = {
            editId,
            isEditMode: !!editId,
            name: '',
            name_ar: '',
            icon: 'fa-tag',
            category: 'general',
            is_active: true,
            sort_order: 0,
            hideSidebar: false,
            isSubmitting: false,
            isLoading: false,
            errors: {},
            iconSearch: '',
            showIconPicker: false,
            categories: []
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        if (this.state.isEditMode) {
            this.loadAttribute();
        }
        this.loadAllCategories()
    }

    async loadAllCategories() {
        const response = await getAllCategories();
        let categories = response.data;
        this.setState({ categories });
    }

    async loadAttribute() {
        this.setState({ isLoading: true });
        try {
            const response = await getAttribute(this.state.editId);
            if (response && response.data) {
                const attr = response.data;
                this.setState({
                    name: attr.name || '',
                    name_ar: attr.name_ar || '',
                    icon: attr.icon || 'fa-tag',
                    category: attr.category || 'general',
                    is_active: attr.is_active !== undefined ? !!attr.is_active : true,
                    sort_order: attr.sort_order || 0,
                });
            }
        } catch (err) {
            console.error('Error loading attribute:', err);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    handleInputChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
        if (this.state.errors[name]) {
            this.setState((prev) => ({ errors: { ...prev.errors, [name]: '' } }));
        }
    };

    selectIcon = (icon) => {
        this.setState({ icon, showIconPicker: false, iconSearch: '' });
    };

    validate = () => {
        const errors = {};
        if (!this.state.name.trim()) errors.name = 'Name (English) is required';
        if (!this.state.category) errors.category = 'Category is required';
        this.setState({ errors });

        if (Object.keys(errors).length > 0) {
            const el =
                document.querySelector(`[name="${Object.keys(errors)[0]}"]`) ||
                document.getElementById(Object.keys(errors)[0]);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        }
        return true;
    };

    handleSubmit = async () => {
        if (!this.validate()) return;
        this.setState({ isSubmitting: true });

        try {
            const payload = {
                name: this.state.name,
                name_ar: this.state.name_ar,
                icon: this.state.icon,
                category: this.state.category,
                is_active: this.state.is_active ? 1 : 0,
                sort_order: this.state.sort_order,
            };

            let response;
            if (this.state.isEditMode) {
                response = await updateAttribute(this.state.editId, payload);
            } else {
                response = await createAttribute(payload);
            }

            if (response && response.success) {
                toast.success(
                    this.state.isEditMode
                        ? 'Attribute updated successfully!'
                        : 'Attribute created successfully!'
                );
                window.location.href = '/attributes';
            } else {
                if (response && response.errors) {
                    const apiErrors = {};
                    Object.keys(response.errors).forEach((key) => {
                        apiErrors[key] = Array.isArray(response.errors[key])
                            ? response.errors[key][0]
                            : response.errors[key];
                    });
                    this.setState({ errors: apiErrors });
                } else {
                    toast.error(response.message || 'An error occurred');
                }
            }
        } catch (err) {
            console.error(err);
            toast.error('An unexpected error occurred');
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    renderFieldError = (fieldName) => {
        if (this.state.errors[fieldName]) {
            return <div className="field-error">{this.state.errors[fieldName]}</div>;
        }
        return null;
    };

    getFilteredIcons = () => {
        const { iconSearch } = this.state;
        if (!iconSearch.trim()) return FA_ICONS;
        const term = iconSearch.toLowerCase().replace('fa-', '');
        return FA_ICONS.filter((ic) => ic.toLowerCase().includes(term));
    };

    render() {
        const {
            isEditMode,
            isLoading,
            isSubmitting,
            showIconPicker,
            categories
        } = this.state;
        const pageTitle = isEditMode
            ? i18n.t('edit_attribute') || 'Edit Attribute'
            : i18n.t('create_attribute') || 'Create Attribute';

        if (isLoading) {
            return (
                <React.Fragment>
                    <HeaderBanner title={pageTitle} subtitle={pageTitle} />
                    <section className="dashboard-section">
                        <div className="container">
                            <div style={{ textAlign: 'center', padding: '4rem' }}>
                                <Loader2 className="animate-spin" size={32} />
                            </div>
                        </div>
                    </section>
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <HeaderBanner title={pageTitle} subtitle={pageTitle} />
                <section className="dashboard-section">
                    <div className="container">
                        <div className="row">
                            <div className="col-xl-3 col-lg-12 col-md-12">
                                {!this.state.hideSidebar && <SideBar />}
                            </div>
                            <div
                                className={
                                    this.state.hideSidebar
                                        ? 'col-xl-12 col-lg-12 col-md-12'
                                        : 'col-xl-9 col-lg-12 col-md-12'
                                }
                            >
                                <div className="card">
                                    <div className="card-header">
                                        <div className="expand-dashboard">
                                            <span
                                                style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                                                onClick={() =>
                                                    this.setState({ hideSidebar: !this.state.hideSidebar })
                                                }
                                            >
                                                {this.state.hideSidebar ? (
                                                    <Shrink strokeWidth={1} />
                                                ) : (
                                                    <Expand strokeWidth={1} />
                                                )}
                                            </span>
                                        </div>
                                        <div className="header-action">
                                            <button
                                                className="button1 button1-gradient button1-back"
                                                onClick={() => (window.location.href = '/attributes')}
                                            >
                                                <ArrowLeft size={18} className="icon" />
                                                <span>{i18n.t('back')}</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="create-group-container">
                                            <div className="form-content">
                                                {/* ========== LEFT COLUMN ========== */}
                                                <div className="left-column">
                                                    <div className="form-section">
                                                        <h2>{pageTitle}</h2>

                                                        <div className="form-row">
                                                            <div className="form-field">
                                                                <label>Name (English) *</label>
                                                                <input
                                                                    type="text"
                                                                    name="name"
                                                                    value={this.state.name}
                                                                    onChange={this.handleInputChange}
                                                                    placeholder="e.g. Area Size"
                                                                    className={this.state.errors.name ? 'error' : ''}
                                                                />
                                                                {this.renderFieldError('name')}
                                                            </div>
                                                            <div className="form-field">
                                                                <label>Name (العربية)</label>
                                                                <input
                                                                    type="text"
                                                                    name="name_ar"
                                                                    value={this.state.name_ar}
                                                                    onChange={this.handleInputChange}
                                                                    placeholder="e.g. المساحة"
                                                                    dir="rtl"
                                                                />
                                                                {this.renderFieldError('name_ar')}
                                                            </div>
                                                        </div>

                                                        <div className="form-row">
                                                            <div className="form-field">
                                                                <label>Category *</label>
                                                                <select
                                                                    name="category"
                                                                    value={this.state.category}
                                                                    onChange={this.handleInputChange}
                                                                >
                                                                    <option value="">--Category--</option>
                                                                    {categories && categories.length > 0 &&
                                                                        categories.map((item) => (
                                                                            <option value={item.id} key={item.id}>
                                                                                {item.category_name}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                            </div>


                                                            <div className="form-field">
                                                                <label>Sort Order</label>
                                                                <input
                                                                    type="number"
                                                                    name="sort_order"
                                                                    value={this.state.sort_order}
                                                                    onChange={this.handleInputChange}
                                                                    placeholder="0"
                                                                    min="0"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="toggle-field">
                                                            <label>Active</label>
                                                            <div
                                                                className={`toggle ${this.state.is_active ? 'active' : ''}`}
                                                                onClick={() =>
                                                                    this.setState({ is_active: !this.state.is_active })
                                                                }
                                                            >
                                                                <div className="toggle-slider"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* ========== RIGHT COLUMN ========== */}
                                                <div className="right-column">
                                                    {/* Icon Picker */}
                                                    <div className="form-section">
                                                        <h2>Icon</h2>

                                                        {/* Selected icon preview */}
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '16px',
                                                                marginBottom: '16px',
                                                                padding: '16px',
                                                                background: '#f9fafb',
                                                                borderRadius: '10px',
                                                                border: '1px solid #e5e7eb',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: '56px',
                                                                    height: '56px',
                                                                    borderRadius: '12px',
                                                                    background: 'rgba(16, 185, 129, 0.1)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                <i
                                                                    className={`fa ${this.state.icon}`}
                                                                    style={{ fontSize: '24px', color: '#10b981' }}
                                                                />
                                                            </div>
                                                            <div style={{ flex: 1 }}>
                                                                <div
                                                                    style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937' }}
                                                                >
                                                                    {this.state.icon}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                                    Currently selected icon
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="button1 button1-secondary"
                                                                style={{ fontSize: '13px', padding: '6px 14px' }}
                                                                onClick={() =>
                                                                    this.setState({ showIconPicker: !showIconPicker })
                                                                }
                                                            >
                                                                {showIconPicker ? 'Close' : 'Change'}
                                                            </button>
                                                        </div>

                                                        {/* Manual entry */}
                                                        <div className="form-field form-field-margin-bottom">
                                                            <label>Icon class (manual)</label>
                                                            <input
                                                                type="text"
                                                                name="icon"
                                                                value={this.state.icon}
                                                                onChange={this.handleInputChange}
                                                                placeholder="e.g. fa-ruler"
                                                            />
                                                        </div>

                                                        {/* Icon grid */}
                                                        {showIconPicker && (
                                                            <div>
                                                                {/* Search icons */}
                                                                <div className="form-field form-field-margin-bottom">
                                                                    <div style={{ position: 'relative' }}>
                                                                        <Search
                                                                            size={16}
                                                                            style={{
                                                                                position: 'absolute',
                                                                                left: '12px',
                                                                                top: '50%',
                                                                                transform: 'translateY(-50%)',
                                                                                color: '#9ca3af',
                                                                            }}
                                                                        />
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Search icons..."
                                                                            value={this.state.iconSearch}
                                                                            onChange={(e) =>
                                                                                this.setState({ iconSearch: e.target.value })
                                                                            }
                                                                            style={{ paddingLeft: '36px' }}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(46px, 1fr))',
                                                                        gap: '6px',
                                                                        maxHeight: '280px',
                                                                        overflowY: 'auto',
                                                                        padding: '4px',
                                                                    }}
                                                                >
                                                                    {this.getFilteredIcons().map((ic) => (
                                                                        <button
                                                                            key={ic}
                                                                            type="button"
                                                                            title={ic}
                                                                            onClick={() => this.selectIcon(ic)}
                                                                            style={{
                                                                                width: '46px',
                                                                                height: '46px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                borderRadius: '8px',
                                                                                border:
                                                                                    this.state.icon === ic
                                                                                        ? '2px solid #10b981'
                                                                                        : '1px solid #e5e7eb',
                                                                                background:
                                                                                    this.state.icon === ic ? '#f0fdf4' : '#fff',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.15s',
                                                                            }}
                                                                        >
                                                                            <i
                                                                                className={`fa ${ic}`}
                                                                                style={{
                                                                                    fontSize: '16px',
                                                                                    color:
                                                                                        this.state.icon === ic ? '#10b981' : '#6b7280',
                                                                                }}
                                                                            />
                                                                        </button>
                                                                    ))}
                                                                </div>

                                                                {this.getFilteredIcons().length === 0 && (
                                                                    <p
                                                                        style={{
                                                                            textAlign: 'center',
                                                                            color: '#9ca3af',
                                                                            padding: '1rem',
                                                                            fontSize: '13px',
                                                                        }}
                                                                    >
                                                                        No icons match your search. You can type any Font Awesome
                                                                        class manually above.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Live Preview */}
                                                    <div className="form-section">
                                                        <h2>Preview</h2>
                                                        <div
                                                            className="enquiry-item"
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '14px',
                                                                marginBottom: 0,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: '44px',
                                                                    height: '44px',
                                                                    borderRadius: '10px',
                                                                    background: 'rgba(16, 185, 129, 0.1)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                <i
                                                                    className={`fa ${this.state.icon || 'fa-tag'}`}
                                                                    style={{ color: '#10b981', fontSize: '18px' }}
                                                                />
                                                            </div>
                                                            <div style={{ flex: 1 }}>
                                                                <div
                                                                    style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937' }}
                                                                >
                                                                    {this.state.name || 'Attribute Name'}
                                                                </div>
                                                                {this.state.name_ar && (
                                                                    <div
                                                                        style={{
                                                                            fontSize: '13px',
                                                                            color: '#6b7280',
                                                                            direction: 'rtl',
                                                                        }}
                                                                    >
                                                                        {this.state.name_ar}
                                                                    </div>
                                                                )}
                                                                <span
                                                                    style={{
                                                                        display: 'inline-block',
                                                                        marginTop: '4px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 500,
                                                                        color: '#10b981',
                                                                        background: '#f0fdf4',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                    }}
                                                                >
                                                                    {this.state.category || 'category'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Submit */}
                                            <div className="action-buttons-l2 border-top-light">
                                                <button
                                                    className="button1 button1-gray"
                                                    onClick={() => (window.location.href = '/attributes')}
                                                    disabled={isSubmitting}
                                                    type="button"
                                                >
                                                    {i18n.t('cancel')}
                                                </button>
                                                <button
                                                    className="button1 button1-primary"
                                                    onClick={this.handleSubmit}
                                                    disabled={isSubmitting}
                                                    type="button"
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <Loader2 className="animate-spin" size={18} />
                                                            <span> Saving...</span>
                                                        </>
                                                    ) : (
                                                        <span>{isEditMode ? 'Update Attribute' : 'Create Attribute'}</span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </React.Fragment>
        );
    }
}